import { useState, useEffect, useCallback, useRef } from 'react';
import { notificationsApi, type NotificationItem } from '@/api/notifications';

const API_BASE = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api`
  : `${window.location.origin}/api`;

// Notification sound — short chime
let audioInstance: HTMLAudioElement | null = null;
function playSound() {
  try {
    if (!audioInstance) {
      audioInstance = new Audio('/notification.wav');
      audioInstance.volume = 0.5;
    }
    audioInstance.currentTime = 0;
    audioInstance.play().catch(() => {});
  } catch {
    // Audio not available
  }
}

export function useNotifications() {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [connected, setConnected] = useState(false);
  const [toast, setToast] = useState<NotificationItem | null>(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const eventSourceRef = useRef<EventSource | null>(null);
  const toastTimerRef = useRef<number | null>(null);
  const soundEnabledRef = useRef(true);

  // Keep ref in sync for use inside SSE callback
  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
  }, [soundEnabled]);

  // Fetch initial data
  const fetchInitial = useCallback(async () => {
    try {
      const [listRes, countRes, prefRes] = await Promise.all([
        notificationsApi.list({ limit: 20 }),
        notificationsApi.unreadCount(),
        notificationsApi.getPreferences(),
      ]);
      setNotifications(listRes.data.data);
      setUnreadCount(countRes.data.count);
      setSoundEnabled(prefRes.data.soundEnabled);
    } catch {
      // Silently fail — will retry on SSE reconnect
    }
  }, []);

  // Connect SSE
  useEffect(() => {
    const authUser = localStorage.getItem('authUser');
    if (!authUser) return;

    fetchInitial();

    const url = `${API_BASE}/notifications/stream`;
    const es = new EventSource(url, { withCredentials: true });

    es.onopen = () => setConnected(true);

    es.onmessage = (event) => {
      try {
        const notif: NotificationItem = JSON.parse(event.data);
        setNotifications((prev) => [notif, ...prev.slice(0, 49)]);
        setUnreadCount((c) => c + 1);

        // Show toast
        setToast(notif);
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        toastTimerRef.current = window.setTimeout(() => setToast(null), 5000);

        // Play sound if enabled
        if (soundEnabledRef.current) playSound();
      } catch {
        // Invalid SSE data
      }
    };

    es.onerror = () => {
      setConnected(false);
      // EventSource auto-reconnects. On reconnect, re-fetch to catch missed notifications.
    };

    // Re-fetch on reconnect (EventSource fires onopen)
    const originalOnOpen = es.onopen;
    let firstOpen = true;
    es.onopen = (e) => {
      if (originalOnOpen) (originalOnOpen as any).call(es, e);
      if (!firstOpen) {
        // This is a reconnection — re-fetch to catch missed notifications
        fetchInitial();
      }
      firstOpen = false;
    };

    eventSourceRef.current = es;

    // NOTE: no visibilitychange reconnect. EventSource already auto-reconnects, and a
    // single connection lives for the tab's lifetime. The previous "pause when hidden,
    // reopen on show" handler spawned a fresh EventSource on every show without reliably
    // closing the prior one, orphaning connections that kept auto-reconnecting in the
    // background — the cause of a server-side SSE connection leak (RSS climb).

    return () => {
      if (eventSourceRef.current) eventSourceRef.current.close();
      eventSourceRef.current = null;
      setConnected(false);
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, [fetchInitial]);

  const markAsRead = useCallback(async (id: string) => {
    try {
      await notificationsApi.markAsRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: true, readAt: new Date().toISOString() } : n)),
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch {
      // Silently fail
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      await notificationsApi.markAllAsRead();
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, read: true, readAt: new Date().toISOString() })),
      );
      setUnreadCount(0);
    } catch {
      // Silently fail
    }
  }, []);

  const dismissToast = useCallback(() => {
    setToast(null);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
  }, []);

  return {
    notifications,
    unreadCount,
    connected,
    toast,
    soundEnabled,
    setSoundEnabled,
    markAsRead,
    markAllAsRead,
    dismissToast,
  };
}
